import React, {useCallback, useEffect, useState} from "react";
import {Alert, Button, ConfigProvider, Input, Modal, Select, Tag, Typography, message} from "antd";
import type {ColumnProps} from "antd/es/table";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {DeleteOutlined, SearchOutlined} from "@ant-design/icons";
import {SummaryTable} from "src/components/SummaryTable";
import {SearchBar} from "src/components/SearchBar";
import {PRIMARY_COLOR} from "src/constants/colorContants";
import {getCachedIngredients} from "src/api/cache/ingredientsCache";
import {Acceptability, Ingredient} from "src/types/ingredient";
import {searchIngredients, sendFeedback} from "src/api/searchApi";
import {TermsCard} from "src/components/TermsCard";
import BottomTermsCard from "src/components/BottomTermsCard";
import {getAcceptabilityIcon, getColumnValue} from "src/utils/containers/SearchContainerUtils";
import {getName, sortItems} from "src/utils/commonUtils";
import {useDebouncedFetch} from "src/hooks/useDebouncedFetch";
import {
    ACCEPTABILITY,
    ADMIN_PAGE,
    adminColumnList,
    HOME_PAGE,
    INGREDIENT,
    MAX_TOKEN_RESULTS,
    MIN_SEARCH_LENGTH,
    SEARCH_DEBOUNCE_DELAY,
    visibilities
} from "src/constants/appConstants";
import {filterIngredient} from "src/utils/types/ingredientUtils";
import Delete from "src/components/common/Delete";
import {searchFiltersStyle, warningBannerStyle, searchActionsContainerStyle, searchBarWrapperStyle} from "src/constants/styleConstants";
import "../css/style.css";
import {getMetricsPublisher, MetricKey} from "src/utils/metrics";

const {Option} = Select;

export let filteredIdsForApi = new Set<string>();

interface SummaryContainerProps {
    admin: boolean;
}

// Helper function to clear all search state
const getEmptySearchState = () => ({
    searchResultsArray: [] as Ingredient[],
    exactSearchResultsArray: [] as Ingredient[],
    exactSearchTokenResultsArray: [] as Ingredient[],
    exactSearchResultsNotFoundArray: [] as string[],
});

const SummaryContainer: React.FC<SummaryContainerProps> = ({admin}) => {
    const initialTime = Date.now();
    useEffect(() => {
        const metricsPublisher = getMetricsPublisher();
        if (admin) {
            metricsPublisher.publishTimerMetric(ADMIN_PAGE,
                MetricKey.PAGE_LOAD_LATENCY, Date.now() - initialTime);
            metricsPublisher.publishCounterMetric(ADMIN_PAGE, MetricKey.CLICKS, 1);
        } else {
            metricsPublisher.publishTimerMetric(HOME_PAGE,
                MetricKey.PAGE_LOAD_LATENCY, Date.now() - initialTime);
            metricsPublisher.publishCounterMetric(HOME_PAGE, MetricKey.CLICKS, 1);
        }
    }, [admin]);
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    
    // Ingredient data
    const [ingredients, setIngredients] = useState<Ingredient[]>([]);
    const [ingredientDetails, setIngredientDetails] = useState<Ingredient>();
    
    // Search input state
    const [searchValue, setSearchValue] = useState<string>("");
    
    // Two-state model: pendingIngredients (user's working list) vs committedIngredients (last searched)
    const [pendingIngredients, setPendingIngredients] = useState<string[]>([]);
    const [committedIngredients, setCommittedIngredients] = useState<string[]>([]);
    const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
    
    // Loading states
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [ingredientIsLoading, setIngredientLoading] = useState<boolean>(true);
    
    // Search results
    const [searchSuggestionsArray, setSearchSuggestionsArray] = useState<Ingredient[]>([]);
    const [searchResultsArray, setSearchResultsArray] = useState<Ingredient[]>([]);
    const [exactSearchResultsArray, setExactSearchResultsArray] = useState<Ingredient[]>([]);
    const [exactSearchTokenResultsArray, setExactSearchTokenResultsArray] = useState<Ingredient[]>([]);
    const [exactSearchResultsNotFoundArray, setExactSearchResultsNotFoundArray] = useState<string[]>([]);
    // Table columns
    const [typeColumns, setTypeColumns] = useState<Map<string, ColumnProps<Ingredient>>>(new Map());
    const [adminColumns, setAdminColumns] = useState<Map<string, ColumnProps<Ingredient>>>(new Map());
    
    // Filters
    const [authors, setAuthors] = useState<string[]>([])
    const [authorFilters, setAuthorFilters] = useState<string[]>([])
    const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
    const [classificationFilters, setClassificationFilters] = useState<string[]>([]);
    const [visibilityFilters, setVisibilityFilters] = useState<string[]>([]);
    const [filterByCharValues, setFilterByCharValues] = useState<string[]>([])
    // Modal states
    const [isDeleteIngredientModalVisible, setDeleteIngredientModalVisible] = useState(false);
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
    const [feedbackText, setFeedbackText] = useState("");
    const handleCloseFeedbackPopup = () => setShowFeedbackPopup(false);
    const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => setFeedbackText(e.target.value);
    const handleSubmitFeedback = async () => {
        try {
            const response = await sendFeedback(committedIngredients, feedbackText, exactSearchResultsArray, exactSearchTokenResultsArray, exactSearchResultsNotFoundArray);
            if (response) {
                message.success(`Feedback Recorded successfully`);
            } else {
                message.error(`Error Recording Feedback 2`);
            }
        } catch (error) {
            message.error(`Error Recording Feedback`);
        } finally {
            setShowFeedbackPopup(false);
            setFeedbackText("");        }
    };

    // Detect pending changes: compare pendingIngredients vs committedIngredients
    useEffect(() => {
        const pending = JSON.stringify([...pendingIngredients].sort());
        const committed = JSON.stringify([...committedIngredients].sort());
        setHasPendingChanges(pending !== committed);
    }, [pendingIngredients, committedIngredients]);

    // When pending ingredients are completely cleared, also clear the summary table
    useEffect(() => {
        if (pendingIngredients.length === 0 && committedIngredients.length > 0) {
            setCommittedIngredients([]);
            const emptyState = getEmptySearchState();
            setSearchResultsArray(emptyState.searchResultsArray);
            setExactSearchResultsArray(emptyState.exactSearchResultsArray);
            setExactSearchTokenResultsArray(emptyState.exactSearchTokenResultsArray);
            setExactSearchResultsNotFoundArray(emptyState.exactSearchResultsNotFoundArray);
            setHasPendingChanges(false);
        }
    }, [pendingIngredients, committedIngredients.length]);


    // Process search results and update state - shared logic for search operations
    const processSearchResults = useCallback((searchResults: any, ingredientsList: string[]) => {
        if (!searchResults) return;
        
        const searchResultsArr: Ingredient[] = [];
        const exactSearchResultsArr: Ingredient[] = [];
        const exactSearchTokenResultsArr: Ingredient[] = [];
        const exactSearchResultsNotFoundArr: string[] = [];
        
        for (const searchVal of ingredientsList) {
            const exactResults: Ingredient[] = searchResults.exactMatchResults?.[searchVal] ?? [];
            
            if (exactResults.length === 0) {
                const tokenResults: Ingredient[] = searchResults.exactMatchTokenResults?.[searchVal] ?? [];
                exactSearchTokenResultsArr.push(...tokenResults.slice(0, MAX_TOKEN_RESULTS));
                exactSearchResultsNotFoundArr.push(searchVal);
            }
            
            const flexibleResults: Ingredient[] = searchResults.flexibleMatchResults?.[searchVal] ?? [];
            exactSearchResultsArr.push(...exactResults);
            searchResultsArr.push(...exactResults, ...flexibleResults);
        }
        
        setExactSearchResultsArray(exactSearchResultsArr);
        setExactSearchTokenResultsArray(exactSearchTokenResultsArr);
        setSearchResultsArray(searchResultsArr);
        setExactSearchResultsNotFoundArray(exactSearchResultsNotFoundArr);
    }, []);

    // Handles explicit search button click - executes search immediately without debouncing
    const handleSearchClick = useCallback(async () => {
        if (selectedType === undefined) {
            message.error("Please select a category to search");
            return;
        }
        
        if (pendingIngredients.length === 0) {
            message.warning("No ingredients to search");
            return;
        }
        
        setIsSearching(true);
        
        try {
            const results = await searchIngredients(pendingIngredients);
            processSearchResults(results, pendingIngredients);
            setCommittedIngredients([...pendingIngredients]);
        } catch (error) {
            console.error("Error executing search:", error);
        } finally {
            setIsSearching(false);
        }
    }, [selectedType, pendingIngredients, processSearchResults]);

    // Type-ahead suggestions with debouncing using reusable hook
    const {isLoading: isFetchingSuggestions} = useDebouncedFetch({
        fetchFn: (signal) => searchIngredients([searchValue], signal),
        delay: SEARCH_DEBOUNCE_DELAY,
        enabled: searchValue.length > MIN_SEARCH_LENGTH,
        deps: [searchValue],
        onSuccess: (results) => {
            const suggestions: Ingredient[] = results?.flexibleMatchResults?.[searchValue] ?? [];
            const uniqueSuggestions = suggestions.filter(
                (ingredient, index, self) => index === self.findIndex((i) => i.id === ingredient.id)
            );
            setSearchSuggestionsArray(uniqueSuggestions);
        },
    });

    // Clear suggestions when search value is too short
    useEffect(() => {
        if (searchValue.length <= MIN_SEARCH_LENGTH) {
            setSearchSuggestionsArray([]);
        }
    }, [searchValue]);

    // Fetch ingredients from API and Cache
    useEffect(() => {
        setIngredientLoading(true);
        getCachedIngredients()
            .then(cachedIngredients => {
                const listOfIngredients = admin ? cachedIngredients : cachedIngredients.filter(ingredient => ingredient.visibility === 'Publish');
                setIngredients(listOfIngredients);
                setIngredientLoading(false);
                
                const types = new Map<string, ColumnProps<Ingredient>>();
                const classifications = new Set<string>();
                const authorList = new Set<string>();
                const adminColumns = new Map<string, ColumnProps<Ingredient>>();
                cachedIngredients.forEach(({classification, acceptability, createdBy}) => {
                    if(classification && classification.trim().length > 0)
                        classifications.add(classification);
                    if (createdBy && createdBy.trim().length > 0)
                        authorList.add(getName(createdBy));
                    acceptability.forEach(a => {
                        if(a.type) {
                            types.set(a.type, {
                                title: a.type,
                                dataIndex: ACCEPTABILITY,
                                key: a.type,
                                render: (value: Acceptability[]) => {
                                    const acceptability = value.find((v: Acceptability) => v.type == a.type)
                                    return getAcceptabilityIcon(acceptability)
                                }
                            })
                        }
                    })
                });
                
                adminColumnList.forEach(({columnName, columnMapping}) => {
                    adminColumns.set(columnName, {
                        title: columnName,
                        dataIndex: columnName,
                        key: columnMapping,
                        render: (_value, record) => <>{getColumnValue(record, columnMapping)}</>
                    });
                })
                setTypeColumns(types);
                setClassificationFilters(Array.from(classifications));
                setAuthors(Array.from(authorList));
                setAuthorFilters(Array.from(authorList));
                setVisibilityFilters(visibilities);
                setAdminColumns(adminColumns);
                
                if (!window.location.href.includes("?ingredients=")) {
                    setPendingIngredients([]);
                    setCommittedIngredients([]);
                    setSearchValue("");
                }
                setFilterByCharValues([]);
            });
    }, [admin, location.pathname]);

    // Fetch query parameters for ingredients from URL and trigger search if ingredients present
    useEffect(() => {
        const searchedIngredients = queryParams.get('ingredients');
        const ingredientsList = searchedIngredients?.split(',').filter(i => i.length > 0) || [];
        
        if (ingredientsList.length > 0) {
            setPendingIngredients(ingredientsList);
            setCommittedIngredients(ingredientsList);
            
            // Execute search for URL-loaded ingredients
            const executeUrlSearch = async () => {
                setIsSearching(true);
                setIngredientLoading(true);
                try {
                    const searchResults = await searchIngredients(ingredientsList);
                    processSearchResults(searchResults, ingredientsList);
                } catch (error) {
                    console.error('Error executing URL search:', error);
                } finally {
                    setIsSearching(false);
                    setIngredientLoading(false);
                }
            };
            
            executeUrlSearch();
        } else {
            setPendingIngredients([]);
            setCommittedIngredients([]);
        }
    }, [queryParams])

    // Remaining Columns
    const remainingColumns = admin ? adminColumns : typeColumns;

    // column headers for table
    let columns: ColumnProps<Ingredient>[] = [
        {
            title: 'Ingredients',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            sorter: sortItems,
            render: (value, record) =>
                <>
                    {
                        admin ? (
                            <>
                                <Tag>{record.classification}</Tag>
                                <p>{value}</p>
                                <Button type="link"><Link to={`edit/ingredient/${record.id}`}>Edit</Link></Button>
                                <ConfigProvider
                                    theme={{
                                        token: {
                                            // Seed Token
                                            colorLink : '#00b966',
                                        },
                                    }}
                                >
                                    <Button type="link"><Link
                                        to={`/?ingredients=${record.name}`}>View</Link></Button>
                                </ConfigProvider>
                                <Button type="link" danger onClick={() => {setIngredientDetails(record); setDeleteIngredientModalVisible(true)}}>Delete</Button>
                            </>
                        ) : (
                            <><Tag>{record.classification}</Tag><p>{value}</p></>
                        )
                    }
                </>
        },
        ...Array.from(remainingColumns.values())
    ];

    const [filteredColumns, setFilteredColumns] = useState(columns);

    // Filter column entries by types
    useEffect(() => {
        setFilteredColumns(columns.filter(c => String(c.key) == 'name' || selectedType === String(c.key)));
    }, [selectedType, filterByCharValues, pendingIngredients])

    const handleDelete = () => {
        setDeleteIngredientModalVisible(false);
        setPendingIngredients([]);
        setCommittedIngredients([]);
        navigate(HOME_PAGE);
        navigate(0);
    }

    const handleCategoryChange = (value: string) => {
        setSelectedType(value);
    };

    const handleSelectChange = (values: any) => {
        // Update the selected value state
        if (selectedType === undefined) {
            console.log("null");
            message.error("Please select a category to search");
            return null;
        }
        setPendingIngredients(values);
        setSearchValue("");
    }

    const tableColumns = admin ? columns : filteredColumns;

    const handleOnSearchValue=(value: string)=>{

        const trimmedValue = value.trim();

        const hasCommaWithNonNumericBefore = /[^0-9]\s*,/;
        const regexToSplit = /(?<!\d),/;

        if (hasCommaWithNonNumericBefore.test(trimmedValue)) {
            const filteredValues = trimmedValue
                .split(regexToSplit)
                .map(v => v.trim())
                .filter(v => v.length > 2);

            if (filteredValues.length > 0) {
                setPendingIngredients([...pendingIngredients, ...filteredValues]);
            }

            setSearchValue("");
            return;
        }
        setSearchValue(value);
    }

    return <>

        <div style={warningBannerStyle}>
            <strong>Note:</strong> The ingredient deck checker is an experimental feature and we are gathering feedback.
        </div>

        <div className={"margin-default"}>
            <h3>Check ingredient acceptability for a product</h3>
        </div>
        <div className={"margin-default"}>
            <Typography.Text>{`Select a category to search `}</Typography.Text>
            <Select
              placeholder={`Select category`}
              style={searchFiltersStyle}
              value={selectedType}
              onChange={handleCategoryChange}
              allowClear
            >
                {
                    admin ? authors
                        .sort((a, b) => a.localeCompare(b, undefined, {numeric: true,}))
                        .map(author => <Option key={author}>{author}</Option>)
                      : Array.from(typeColumns.keys())
                        .sort((a, b) => a.localeCompare(b, undefined, {numeric: true,}))
                        .map(type => <Option key={type}>{type}</Option>)
                }
            </Select>
        </div>
        <div className={"margin-default"}>
            <div style={searchActionsContainerStyle}>
                <div style={searchBarWrapperStyle}>
                    <SearchBar
                      data={
                          (searchValue.length > 2 ? searchSuggestionsArray : ingredients)
                            .sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true,}))
                            .map(ingredient => <Option key={`${ingredient.name}-${ingredient.id}`}
                                                       value={`${ingredient.name}`}>{ingredient.name}</Option>)
                      }
                      searchValue={searchValue}
                      searchedValues={pendingIngredients}
                      placeholder="Type or paste ingredients separated by a comma to search"
                      loading={isFetchingSuggestions}
                      onChange={handleSelectChange}
                      onSearch={value => {

                          if (selectedType !== undefined) {
                              handleOnSearchValue(value);
                          } else {
                              message.error("Please select a category to search");
                          }
                      }}
                      onInputKeyDown={e => {
                          if (e.key === 'Enter') {

                              e.stopPropagation()

                              if (selectedType === undefined) {
                                  console.log("null");
                                  message.error("Please select a category to search");
                                  return null;
                              }
                              // @ts-ignore
                              if (e.target.value.length > 0) {
                                  // @ts-ignore
                                  if (searchValue.trim().length > 2 && selectedType !== undefined) {
                                      setPendingIngredients([...pendingIngredients, searchValue.trim()]);
                                  }
                                  setSearchValue("");
                              }
                          }
                      }}
                      tagRender={props => {
                          const {value, closable, onClose} = props;
                          const [tagColor, setTagColor] = useState(PRIMARY_COLOR); // Initial state as primary color
                          const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
                              event.preventDefault();
                              event.stopPropagation();
                          };

                          return (
                            <Tag
                              color={tagColor}
                              onMouseDown={onPreventMouseDown}
                              closable={closable}
                              onClose={onClose}
                            >
                                {value}
                            </Tag>
                          );
                      }
                      }
                    />
                </div>
                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearchClick}
                    loading={isSearching}
                    disabled={pendingIngredients.length === 0 || selectedType === undefined}
                >
                    Search
                </Button>
                <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        setCommittedIngredients([]);
                        setPendingIngredients([]);
                        setSearchValue("");
                    }}
                    disabled={pendingIngredients.length === 0}
                >
                    Clear All
                </Button>
            </div>
            <div className={"margin-default"}>
                <TermsCard/>
            </div>
        </div>

        <div style={{
            backgroundColor: "#f0f8ff",
            padding: "10px",
            marginBottom: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
        }}>
            <a
              href="https://forms.office.com/Pages/ResponsePage.aspx?id=YX-gMUrJmE-jBFdL_CvhuuTkObczpJJFiYEXgb_FMg5UMzMzU0hWRElRUVRZMjlIRTdUQ0pNVjVFQS4u"
              target="_blank"
              rel="noopener noreferrer"
              style={{color: "#1890ff", textDecoration: "none"}}
            >
                Tell us your feedback or report missing ingredient
            </a>
        </div>

        {showFeedbackPopup && (
          <Modal
            title="Provide Your Feedback"
            visible={showFeedbackPopup}
            onOk={handleSubmitFeedback}
            onCancel={handleCloseFeedbackPopup}
          >
              <Input
                placeholder="Type your feedback here..."
                value={feedbackText}
                onChange={handleFeedbackChange}
              />
          </Modal>
        )}

        <div>
            <h3>Summary Table:</h3>
            {hasPendingChanges && (
                <Alert
                    message="You have unsearched changes. Click Search to update results."
                    type="warning"
                    showIcon
                />
            )}
        </div>
        <SummaryTable
          data={
              filterIngredient(
                committedIngredients.length > 0 ? exactSearchResultsArray : [],
                classificationFilters,
                authorFilters,
                visibilityFilters,
                selectedType ? selectedType : "Food"
              )}
          unreviewedData={exactSearchResultsNotFoundArray}
          closeMatchData={
              filterIngredient(
                committedIngredients.length > 0 ? exactSearchTokenResultsArray : [],
                classificationFilters,
                authorFilters,
                visibilityFilters,
                selectedType?.length ? selectedType : "Food"
              )}
          category={selectedType ? selectedType : "Food"}
          columns={tableColumns}
          loading={ingredientIsLoading || isSearching}/>

        <div className={"margin-default"}>
            <BottomTermsCard/>
        </div>
        {
          ingredientDetails !== undefined && (
            <Delete
              open={isDeleteIngredientModalVisible}
              onConfirm={handleDelete}
              onCancel={() => setDeleteIngredientModalVisible(false)}
              type={INGREDIENT}
              id={ingredientDetails?.id}
            />
          )
        }
    </>
}

export default SummaryContainer;